// src/pages/public/VillasPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MapPin, Bed, Bath, Users, Star, SlidersHorizontal, X, ArrowUpDown, Search, MessageCircle, Eye, ChevronDown, ArrowRight } from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import VillaFilter from '../../components/villa/VillaFilter';
import VillaDetailPage from './VillaDetailPage';
import Loading from '../../components/common/Loading';
import Pagination from '../../components/common/Pagination';

/* ── Color tokens (same as HomePage) ── */
const C = {
  amber:      '#d97706',
  amberLight: '#fef3c7',
  amberDark:  '#92400e',
  amberHover: '#b45309',
  white:      '#ffffff',
  gray50:     '#f9fafb',
  gray100:    '#f3f4f6',
  gray200:    '#e5e7eb',
  gray300:    '#d1d5db',
  gray400:    '#9ca3af',
  gray500:    '#6b7280',
  gray600:    '#4b5563',
  gray700:    '#374151',
  gray900:    '#111827',
  stone50:    '#fafaf9',
  green:      '#16a34a',
  greenHover: '#15803d',
};

/* ── useInView hook ── */
const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
};

/* ── Animated wrapper ── */
const Anim = ({ children, delay = 0, dir = 'up', style = {} }) => {
  const [ref, inView] = useInView();
  const from = {
    up:    'translateY(40px)',
    left:  'translateX(-40px)',
    right: 'translateX(40px)',
    scale: 'scale(0.9)',
  };
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'none' : from[dir],
      transition: `opacity 0.75s cubic-bezier(0.16,1,0.3,1) ${delay}ms, transform 0.75s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      ...style,
    }}>
      {children}
    </div>
  );
};

/* ── Floating Orb ── */
const Orb = ({ size, color, top, left, delay = 0 }) => (
  <div style={{
    position: 'absolute', width: size, height: size, borderRadius: '50%',
    background: color, top, left, filter: 'blur(70px)', opacity: 0.28,
    animation: `floatOrb ${7 + delay}s ease-in-out infinite alternate`,
    animationDelay: `${delay}s`, pointerEvents: 'none', zIndex: 0,
  }} />
);

/* ── Primary Button ── */
const BtnPrimary = ({ onClick, children, style = {}, type = 'button' }) => {
  const [hov, setHov] = useState(false);
  return (
    <button type={type} onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '11px 22px', borderRadius: 50, border: 'none', cursor: 'pointer',
        backgroundColor: hov ? C.amberHover : C.amber,
        color: C.white, fontWeight: 600, fontSize: 14, fontFamily: 'inherit',
        transform: hov ? 'translateY(-2px)' : 'translateY(0)',
        boxShadow: hov ? '0 12px 28px rgba(217,119,6,0.45)' : '0 4px 14px rgba(217,119,6,0.28)',
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
        ...style,
      }}>
      {children}
    </button>
  );
};

/* ── Outline Button ── */
const BtnOutline = ({ onClick, children, dark = false, style = {} }) => {
  const [hov, setHov] = useState(false);
  return (
    <button onClick={onClick}
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '10px 22px', borderRadius: 50, cursor: 'pointer',
        backgroundColor: hov ? (dark ? 'rgba(255,255,255,0.1)' : 'rgba(217,119,6,0.07)') : 'transparent',
        color: dark ? C.white : C.amber,
        border: `2px solid ${dark ? 'rgba(255,255,255,0.45)' : C.amber}`,
        fontWeight: 600, fontSize: 14, fontFamily: 'inherit',
        transform: hov ? 'translateY(-2px)' : 'translateY(0)',
        transition: 'all 0.3s cubic-bezier(0.34,1.56,0.64,1)',
        ...style,
      }}>
      {children}
    </button>
  );
};

/* ── Section Label ── */
const SectionLabel = ({ text }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center', marginBottom: 16 }}>
    <div style={{ width: 36, height: 3, background: `linear-gradient(90deg, ${C.amber}, #f59e0b)`, borderRadius: 2 }} />
    <span style={{ fontSize: 11, color: C.amber, fontWeight: 700, letterSpacing: '0.15em', textTransform: 'uppercase' }}>{text}</span>
    <div style={{ width: 36, height: 3, background: `linear-gradient(90deg, #f59e0b, ${C.amber})`, borderRadius: 2 }} />
  </div>
);

/* ══════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════ */
const VillasPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const videoRef = useRef(null);

  const [filters, setFilters] = useState({
    category:   searchParams.get('category')   || '',
    location:   searchParams.get('location')   || '',
    minPrice:   searchParams.get('minPrice')   || '',
    maxPrice:   searchParams.get('maxPrice')   || '',
    bedrooms:   searchParams.get('bedrooms')   || '',
    bathrooms:  searchParams.get('bathrooms')  || '',
    search:     searchParams.get('search')     || '',
    featured:   searchParams.get('featured')   || '',
    facilities: searchParams.get('facilities') || '',
  });

  const [properties, setProperties]       = useState([]);
  const [dropdownData, setDropdownData]   = useState({ categories: [], locations: [], facilities: [] });
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [showFilters, setShowFilters]     = useState(false);
  const [sortBy, setSortBy]               = useState('created_at');
  const [sortOrder, setSortOrder]         = useState('desc');
  const [currentPage, setCurrentPage]     = useState(1);
  const [totalPages, setTotalPages]       = useState(1);
  const [totalProperties, setTotalProperties] = useState(0);
  const [selectedVilla, setSelectedVilla] = useState(null);
  const [showModal, setShowModal]         = useState(false);
  const [hoveredVilla, setHoveredVilla]   = useState(null);
  const [scrollY, setScrollY]             = useState(0);
  const [mousePos, setMousePos]           = useState({ x: 0, y: 0 });
  const itemsPerPage = 12;

  useEffect(() => {
    const onScroll = () => setScrollY(window.scrollY);
    const onMouse  = e  => setMousePos({ x: e.clientX, y: e.clientY });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('mousemove', onMouse, { passive: true });
    return () => { window.removeEventListener('scroll', onScroll); window.removeEventListener('mousemove', onMouse); };
  }, []);

  const heroOpacity  = Math.max(0, 1 - scrollY / 500);
  const heroParallax = scrollY * 0.3;

  const fetchDropdownData = async () => {
    try {
      const r = await fetch(`${process.env.REACT_APP_API_URL}/dropdown/all`);
      if (!r.ok) throw new Error('Failed');
      const d = await r.json();
      if (d.success) setDropdownData({ categories: d.data.categories || [], locations: d.data.locations || [], facilities: d.data.facilities || [] });
    } catch (e) { console.error(e); }
  };

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: currentPage.toString(), limit: itemsPerPage.toString(), status: 'active' });
      Object.entries(filters).forEach(([k, v]) => { if (v && v !== '') params.set(k, v); });
      if (sortBy) params.set('sortBy', sortBy);
      if (sortOrder) params.set('sortOrder', sortOrder);

      const r = await fetch(`${process.env.REACT_APP_API_URL}/properties?${params}`);
      if (!r.ok) throw new Error('Failed to fetch properties');
      const d = await r.json();
      setProperties(d.properties || []);
      setTotalProperties(d.total || 0);
      setTotalPages(d.totalPages || 1);
      setError(null);
    } catch (e) { console.error(e); setError(e.message); setProperties([]); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchDropdownData(); }, []);
  useEffect(() => { fetchProperties(); }, [filters, currentPage, sortBy, sortOrder]);
  useEffect(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== '') p.set(k, v); });
    if (sortBy !== 'created_at') p.set('sortBy', sortBy);
    if (sortOrder !== 'desc') p.set('sortOrder', sortOrder);
    setSearchParams(p);
    setCurrentPage(1);
  }, [filters, sortBy, sortOrder, setSearchParams]);

  const handleFilterChange = (newFilters) => setFilters(prev => ({ ...prev, ...newFilters }));
  const handleSearch = (term) => setFilters(prev => ({ ...prev, search: term }));
  const clearFilters = () => { setFilters({ category:'', location:'', minPrice:'', maxPrice:'', bedrooms:'', bathrooms:'', search:'', featured:'', facilities:'' }); setSortBy('created_at'); setSortOrder('desc'); };
  const getActiveFiltersCount = () => Object.values(filters).filter(v => v && v !== '').length;
  const formatCurrency = (amount, currency = 'IDR') => {
    if (!amount) return '-';
    return currency === 'IDR'
      ? new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(amount)
      : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  /* ── Error state ── */
  if (error) return (
    <MainLayout>
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'DM Sans', sans-serif" }}>
        <div style={{ textAlign: 'center', padding: 40 }}>
          <div style={{ fontSize: 56, marginBottom: 20 }}>🏚️</div>
          <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 32, fontWeight: 700, color: C.gray900, marginBottom: 12 }}>Something Went Wrong</h2>
          <p style={{ color: C.gray500, marginBottom: 28 }}>Failed to load villa data. Please try again.</p>
          <BtnPrimary onClick={fetchProperties} style={{ padding: '13px 28px', fontSize: 15 }}>
            <Search size={16} /> Try Again
          </BtnPrimary>
        </div>
      </div>
    </MainLayout>
  );

  return (
    <MainLayout>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
        *, *::before, *::after { box-sizing: border-box; }

        @keyframes floatOrb {
          0%   { transform: translateY(0px)   scale(1);   }
          100% { transform: translateY(-38px) scale(1.1); }
        }
        @keyframes heroIn {
          from { opacity: 0; transform: translateY(36px); }
          to   { opacity: 1; transform: translateY(0);    }
        }
        @keyframes shimmer {
          0%   { background-position: -200% center; }
          100% { background-position:  200% center; }
        }
        @keyframes pulseDot {
          0%, 100% { box-shadow: 0 0 0 0    rgba(217,119,6,0.6); }
          50%      { box-shadow: 0 0 0 10px rgba(217,119,6,0);   }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0px);  opacity: 1;   }
          50%      { transform: translateY(9px);  opacity: 0.4; }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-12px); }
          to   { opacity: 1; transform: translateY(0); }
        }

        .hero-1 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.2s both; }
        .hero-2 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.45s both; }
        .hero-3 { animation: heroIn 1.1s cubic-bezier(0.16,1,0.3,1) 0.7s both; }

        .shimmer-text {
          background: linear-gradient(90deg, #d97706, #f59e0b, #d97706);
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmer 3s linear infinite;
        }

        .villa-card {
          transition: transform 0.5s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.4s ease;
        }
        .villa-card:hover {
          transform: translateY(-12px) scale(1.015) !important;
          box-shadow: 0 36px 72px rgba(0,0,0,0.15) !important;
        }

        .filter-btn {
          transition: all 0.3s cubic-bezier(0.34,1.56,0.64,1);
        }
        .filter-btn:hover { transform: translateY(-1px); }

        .sort-select {
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }
        .sort-select:focus {
          outline: none;
          border-color: #d97706;
          box-shadow: 0 0 0 3px rgba(217,119,6,0.15);
        }

        .search-input:focus {
          outline: none;
          box-shadow: 0 0 0 4px rgba(217,119,6,0.2);
        }

        .facility-tag {
          transition: transform 0.2s ease, background 0.2s ease;
        }
        .facility-tag:hover { transform: scale(1.05); background: rgba(217,119,6,0.18) !important; }

        .scroll-bounce { animation: bounce 2s ease-in-out infinite; }

        .cursor-glow {
          pointer-events: none; position: fixed;
          width: 260px; height: 260px; border-radius: 50%;
          background: radial-gradient(circle, rgba(217,119,6,0.06) 0%, transparent 70%);
          transform: translate(-50%, -50%);
          transition: left 0.12s ease, top 0.12s ease;
          z-index: 9999; mix-blend-mode: multiply;
        }

        .filter-panel {
          animation: slideDown 0.4s cubic-bezier(0.16,1,0.3,1);
        }

        .whatsapp-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 28px rgba(22,163,74,0.4) !important; }
        .whatsapp-btn { transition: all 0.3s cubic-bezier(0.34,1.56,0.64,1); }

        .cta-img { transition: transform 0.8s ease; }
        .cta-img:hover { transform: scale(1.03); }
      `}</style>

      {/* Cursor glow */}
      <div className="cursor-glow" style={{ left: mousePos.x, top: mousePos.y }} />

      {/* ══ HERO ══ */}
      <section style={{ position: 'relative', height: '60vh', minHeight: 480, overflow: 'hidden' }}>
        <video ref={videoRef} autoPlay muted loop playsInline style={{
          width: '100%', height: '100%', objectFit: 'cover',
          transform: `translateY(${heroParallax}px)`, willChange: 'transform',
        }}>
          <source src="/images/hero-video.mp4" type="video/mp4" />
          <img src="https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?w=1920&q=80"
            alt="Luxury Villa" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </video>

        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.55), rgba(0,0,0,0.75))' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.2), transparent)' }} />
        <Orb size="380px" color="radial-gradient(circle, #d97706, #f59e0b)" top="5%"  left="65%" delay={0} />
        <Orb size="250px" color="radial-gradient(circle, #92400e, #d97706)" top="55%" left="-3%" delay={2} />

        <div style={{
          position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center', textAlign: 'center',
          padding: '0 24px', opacity: heroOpacity,
          transform: `translateY(${heroParallax * 0.35}px)`,
        }}>
          {/* Badge */}
          <div className="hero-1" style={{ marginBottom: 22 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 10, padding: '9px 20px',
              backgroundColor: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.22)', borderRadius: 50,
              color: C.white, fontSize: 11, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase',
              fontFamily: "'DM Sans', sans-serif",
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: C.amber, animation: 'pulseDot 2s ease-in-out infinite' }} />
              Dewanta Property Collection
            </span>
          </div>

          <h1 className="hero-2" style={{
            fontFamily: "'Cormorant Garamond', serif",
            fontSize: 'clamp(38px, 7vw, 78px)', fontWeight: 700,
            color: C.white, lineHeight: 1.05, letterSpacing: '-0.02em', marginBottom: 16,
          }}>
            Villa <span className="shimmer-text">Collection</span>
          </h1>

          <p className="hero-2" style={{
            fontSize: 'clamp(15px, 2vw, 19px)', color: 'rgba(255,255,255,0.82)',
            maxWidth: 560, marginBottom: 36, fontWeight: 300, lineHeight: 1.65,
            fontFamily: "'DM Sans', sans-serif",
          }}>
            Discover your dream villa in Bali with world-class facilities and breathtaking views
          </p>

          {/* Search bar */}
          <div className="hero-3" style={{ width: '100%', maxWidth: 580 }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search villas by name, location, or facilities..."
                value={filters.search}
                onChange={e => handleSearch(e.target.value)}
                className="search-input"
                style={{
                  width: '100%', padding: '16px 140px 16px 22px',
                  backgroundColor: C.white, color: C.gray900,
                  borderRadius: 50, border: 'none', fontSize: 15,
                  fontFamily: "'DM Sans', sans-serif",
                  boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
                }}
              />
              <BtnPrimary
                onClick={fetchProperties}
                style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', padding: '10px 22px', fontSize: 14, borderRadius: 50 }}
              >
                <Search size={15} /> Search
              </BtnPrimary>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="scroll-bounce" style={{ position: 'absolute', bottom: 28, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, color: 'rgba(255,255,255,0.6)', fontFamily: "'DM Sans', sans-serif" }}>
          <span style={{ fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase' }}>Explore</span>
          <ChevronDown size={17} />
        </div>
      </section>

      {/* ══ MAIN CONTENT ══ */}
      <section style={{ padding: '48px 24px 80px', backgroundColor: C.stone50, position: 'relative', overflow: 'hidden' }}>
        <Orb size="500px" color="radial-gradient(circle, rgba(217,119,6,0.07), transparent)" top="-5%" left="70%" delay={1} />

        <div style={{ maxWidth: 1280, margin: '0 auto', position: 'relative', zIndex: 1, fontFamily: "'DM Sans', sans-serif" }}>

          {/* ── Toolbar ── */}
          <Anim delay={0}>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 16, marginBottom: 32 }}>

              {/* Left: filter toggle + clear */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button className="filter-btn" onClick={() => setShowFilters(!showFilters)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '10px 18px', borderRadius: 50, border: `1.5px solid ${C.gray200}`,
                  backgroundColor: showFilters ? C.amberLight : C.white,
                  color: showFilters ? C.amberDark : C.gray700,
                  fontWeight: 600, fontSize: 14, cursor: 'pointer', fontFamily: 'inherit',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}>
                  <SlidersHorizontal size={16} />
                  Filters
                  {getActiveFiltersCount() > 0 && (
                    <span style={{ backgroundColor: C.amber, color: C.white, borderRadius: '50%', width: 20, height: 20, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>
                      {getActiveFiltersCount()}
                    </span>
                  )}
                </button>

                {getActiveFiltersCount() > 0 && (
                  <button onClick={clearFilters} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    color: C.amber, background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                    transition: 'color 0.2s',
                  }}>
                    <X size={14} /> Clear All
                  </button>
                )}
              </div>

              {/* Right: results count + sort */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                {!loading && (
                  <span style={{ color: C.gray500, fontSize: 14 }}>
                    Showing <strong style={{ color: C.gray900 }}>{((currentPage - 1) * itemsPerPage) + 1}–{Math.min(currentPage * itemsPerPage, totalProperties)}</strong> of <strong style={{ color: C.gray900 }}>{totalProperties}</strong> villas
                  </span>
                )}

                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ArrowUpDown size={14} color={C.gray500} />
                  <select
                    className="sort-select"
                    value={`${sortBy}-${sortOrder}`}
                    onChange={e => {
                      const [field, order] = e.target.value.split('-');
                      setSortBy(field); setSortOrder(order);
                    }}
                    style={{
                      padding: '9px 14px', borderRadius: 50,
                      border: `1.5px solid ${C.gray200}`,
                      backgroundColor: C.white, color: C.gray700,
                      fontSize: 14, fontFamily: 'inherit', cursor: 'pointer',
                    }}
                  >
                    <option value="created_at-desc">Latest</option>
                    <option value="created_at-asc">Oldest</option>
                    <option value="daily_price_idr-asc">Lowest Price</option>
                    <option value="daily_price_idr-desc">Highest Price</option>
                    <option value="title-asc">Name A–Z</option>
                    <option value="title-desc">Name Z–A</option>
                  </select>
                </div>
              </div>
            </div>
          </Anim>

          {/* ── Main grid ── */}
          <div style={{ display: 'flex', gap: 32, alignItems: 'flex-start' }}>

            {/* Sidebar */}
            {showFilters && (
              <div className="filter-panel" style={{
                width: 280, flexShrink: 0,
                backgroundColor: C.white, borderRadius: 20,
                boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
                border: `1px solid ${C.gray100}`,
                padding: 24, position: 'sticky', top: 24,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                  <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700, color: C.gray900 }}>Filters</h3>
                  <button onClick={() => setShowFilters(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.gray400, transition: 'color 0.2s' }}
                    onMouseEnter={e => e.currentTarget.style.color = C.gray700}
                    onMouseLeave={e => e.currentTarget.style.color = C.gray400}>
                    <X size={18} />
                  </button>
                </div>
                <VillaFilter
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  categories={dropdownData.categories}
                  locations={dropdownData.locations}
                  facilities={dropdownData.facilities}
                  onClearFilters={clearFilters}
                />
              </div>
            )}

            {/* Villa grid */}
            <div style={{ flex: 1, minWidth: 0 }}>
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 280 }}>
                  <div style={{ width: 50, height: 50, border: `4px solid ${C.amberLight}`, borderTopColor: C.amber, borderRadius: '50%', animation: 'spin 0.9s linear infinite' }} />
                </div>
              ) : properties.length > 0 ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 28, marginBottom: 40 }}>
                    {properties.map((villa, i) => {
                      const hov = hoveredVilla === villa.id;
                      return (
                        <Anim key={villa.id} delay={(i % 6) * 80}>
                          <div
                            className="villa-card"
                            onClick={() => { setSelectedVilla(villa); setShowModal(true); }}
                            onMouseEnter={() => setHoveredVilla(villa.id)}
                            onMouseLeave={() => setHoveredVilla(null)}
                            style={{ backgroundColor: C.white, borderRadius: 20, overflow: 'hidden', boxShadow: '0 6px 28px rgba(0,0,0,0.08)', cursor: 'pointer' }}
                          >
                            {/* Image */}
                            <div style={{ position: 'relative', height: 240, overflow: 'hidden' }}>
                              <img
                                src={villa.primary_image || (villa.images?.[0]?.image_path) || '/placeholder-villa.jpg'}
                                alt={villa.title}
                                style={{ width: '100%', height: '100%', objectFit: 'cover', transform: hov ? 'scale(1.1)' : 'scale(1)', transition: 'transform 0.8s cubic-bezier(0.16,1,0.3,1)' }}
                                onError={e => { e.target.src = '/placeholder-villa.jpg'; }}
                              />
                              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.52), transparent)', opacity: hov ? 1 : 0, transition: 'opacity 0.5s' }} />

                              {villa.featured && (
                                <span style={{ position: 'absolute', top: 13, left: 13, padding: '5px 13px', backgroundColor: C.amber, color: C.white, borderRadius: 50, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', fontFamily: "'DM Sans', sans-serif" }}>
                                  Featured
                                </span>
                              )}
                              <span style={{ position: 'absolute', top: 13, right: 13, padding: '5px 11px', backgroundColor: 'rgba(255,255,255,0.92)', borderRadius: 50, fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3, fontFamily: "'DM Sans', sans-serif" }}>
                                <Star size={12} fill="#fbbf24" color="#fbbf24" /> 5.0
                              </span>

                              {/* Hover CTA */}
                              <div style={{ position: 'absolute', bottom: 16, left: 0, right: 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6, color: C.white, fontWeight: 600, fontSize: 13, opacity: hov ? 1 : 0, transform: hov ? 'translateY(0)' : 'translateY(14px)', transition: 'all 0.4s ease', fontFamily: "'DM Sans', sans-serif" }}>
                                <Eye size={14} /> View Details
                              </div>
                            </div>

                            {/* Info */}
                            <div style={{ padding: 20 }}>
                              <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, fontWeight: 700, color: C.gray900, marginBottom: 4 }}>{villa.title}</h3>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: C.gray500, fontSize: 13, marginBottom: 10, fontFamily: "'DM Sans', sans-serif" }}>
                                <MapPin size={13} color={C.amber} /> {villa.location_name}
                              </div>

                              <p style={{ color: C.gray500, fontSize: 13, lineHeight: 1.65, marginBottom: 12, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', fontFamily: "'DM Sans', sans-serif" }}>
                                {villa.description}
                              </p>

                              <div style={{ display: 'flex', gap: 16, color: C.gray600, fontSize: 13, marginBottom: 12, fontFamily: "'DM Sans', sans-serif" }}>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Bed size={13} /> {villa.bedrooms}</span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Bath size={13} /> {villa.bathrooms}</span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}><Users size={13} /> {villa.max_guests}</span>
                              </div>

                              {/* Facilities */}
                              {villa.facilities?.length > 0 && (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
                                  {villa.facilities.slice(0, 3).map(f => (
                                    <span key={f.id} className="facility-tag" style={{ backgroundColor: C.amberLight, color: C.amberDark, fontSize: 11, padding: '3px 10px', borderRadius: 50, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
                                      {f.name}
                                    </span>
                                  ))}
                                  {villa.facilities.length > 3 && (
                                    <span style={{ backgroundColor: C.gray100, color: C.gray600, fontSize: 11, padding: '3px 10px', borderRadius: 50, fontWeight: 600, fontFamily: "'DM Sans', sans-serif" }}>
                                      +{villa.facilities.length - 3} more
                                    </span>
                                  )}
                                </div>
                              )}

                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 14, borderTop: `1px solid ${C.gray100}` }}>
                                <div>
                                  <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700, color: C.amber }}>
                                    IDR {new Intl.NumberFormat('id-ID').format(villa.daily_price_idr)}
                                  </div>
                                  <div style={{ fontSize: 11, color: C.gray400, textTransform: 'uppercase', letterSpacing: '0.1em', fontFamily: "'DM Sans', sans-serif" }}>per night</div>
                                </div>
                                <BtnPrimary style={{ padding: '9px 16px', fontSize: 13 }}>
                                  <Eye size={13} /> View Details
                                </BtnPrimary>
                              </div>
                            </div>
                          </div>
                        </Anim>
                      );
                    })}
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <Anim delay={0}>
                      <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                      </div>
                    </Anim>
                  )}
                </>
              ) : (
                /* Empty state */
                <Anim delay={0}>
                  <div style={{ textAlign: 'center', padding: '64px 24px', backgroundColor: C.white, borderRadius: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.06)' }}>
                    <div style={{ width: 80, height: 80, margin: '0 auto 20px', backgroundColor: C.gray100, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 36 }}>🏡</div>
                    <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 700, color: C.gray900, marginBottom: 10 }}>No Villas Found</h3>
                    <p style={{ color: C.gray500, fontSize: 15, marginBottom: 28, lineHeight: 1.6, fontFamily: "'DM Sans', sans-serif" }}>
                      Try adjusting your filters or clear them to see more villas.
                    </p>
                    <BtnPrimary onClick={clearFilters} style={{ padding: '13px 28px', fontSize: 15 }}>
                      <X size={16} /> Clear All Filters
                    </BtnPrimary>
                  </div>
                </Anim>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ══ CTA SECTION ══ */}
      <section style={{ padding: '100px 24px', backgroundColor: C.white, position: 'relative', overflow: 'hidden', fontFamily: "'DM Sans', sans-serif" }}>
        <Orb size="450px" color="radial-gradient(circle, rgba(217,119,6,0.08), transparent)" top="-10%" left="-8%" delay={2} />
        <Orb size="350px" color="radial-gradient(circle, rgba(251,191,36,0.07), transparent)" top="30%"  left="70%" delay={4} />

        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px,1fr))', gap: 72, alignItems: 'center', position: 'relative', zIndex: 1 }}>
          <div>
            <Anim dir="left" delay={0}>
              <SectionLabel text="We're Here to Help" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 'clamp(30px,4.5vw,48px)', fontWeight: 700, color: C.gray900, lineHeight: 1.15, marginBottom: 18 }}>
                Can't Find the<br /><span style={{ color: C.amber }}>Perfect Villa?</span>
              </h2>
            </Anim>
            <Anim dir="left" delay={100}>
              <p style={{ fontSize: 16, color: C.gray600, lineHeight: 1.8, marginBottom: 12 }}>
                Our team is ready to help you find your dream villa with complimentary consultation services.
              </p>
              <p style={{ fontSize: 15, color: C.gray500, lineHeight: 1.8, marginBottom: 32 }}>
                We specialize in matching guests with their ideal Bali accommodation, taking into account your preferences, budget, and special requirements.
              </p>
            </Anim>
            <Anim dir="left" delay={180}>
              <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
                <BtnPrimary onClick={() => window.location.href = '/contact'} style={{ padding: '13px 26px', fontSize: 15 }}>
                  <MessageCircle size={17} /> Contact Us
                </BtnPrimary>
                <a
                  href="https://wa.me/6287785686777"
                  target="_blank" rel="noopener noreferrer"
                  className="whatsapp-btn"
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                    padding: '13px 26px', borderRadius: 50,
                    backgroundColor: C.green, color: C.white,
                    fontWeight: 600, fontSize: 15, textDecoration: 'none',
                    boxShadow: '0 4px 14px rgba(22,163,74,0.28)',
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  <svg width="17" height="17" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                  </svg>
                  WhatsApp Us
                </a>
              </div>
            </Anim>
          </div>

          <Anim dir="right" delay={200}>
            <div style={{ position: 'relative' }}>
              <img src="/images/bawah.jpg" alt="Luxury Villa Experience"
                className="cta-img"
                style={{ width: '100%', borderRadius: 24, boxShadow: '0 30px 80px rgba(0,0,0,0.15)', display: 'block' }} />
              <div style={{ position: 'absolute', top: -14, right: -14, width: 80, height: 80, border: `4px solid ${C.amber}`, borderRadius: 18, opacity: 0.3, zIndex: -1, animation: 'floatOrb 5s ease-in-out infinite alternate' }} />
              <div style={{ position: 'absolute', bottom: -12, left: -12, width: 52, height: 52, backgroundColor: C.amberLight, borderRadius: 14, zIndex: -1, animation: 'floatOrb 7s ease-in-out infinite alternate', animationDelay: '2s' }} />
            </div>
          </Anim>
        </div>
      </section>

      {/* Villa Detail Modal */}
      {showModal && selectedVilla && (
        <VillaDetailPage
          villa={selectedVilla}
          onClose={() => { setShowModal(false); setSelectedVilla(null); }}
          formatCurrency={formatCurrency}
          isModal={true}
        />
      )}
    </MainLayout>
  );
};

export default VillasPage;